<script setup lang="ts">
import { computed } from "vue";
import { RouterLink } from "vue-router";

import type { ButtonProps } from "@/dsComponents/buttons/types";

import Loader from "@/dsComponents/Loader.vue";

const props = withDefaults(defineProps<ButtonProps>(), {
    state: "default",
    size: "xl",
    type: "primary",
    variant: "filled",
    shadow: false,
});

const emit = defineEmits(["click"]);

const click = (event: PointerEvent) => {
    emit("click", event);
};

const classes = computed(() => [
    "ds-button",
    props.size,
    props.type,
    props.variant,
    props.state,
    {
        iconOnly: props.iconOnly,
        badge: props.badge,
        fullWidth: props.fullWidth,
        disabled: props.loading || props.disabled,
        focused: props.focused,
        readonly: props.readonly,
        shadow: props.shadow,
    },
]);
</script>
<template>
    <component :is="to ? RouterLink : 'button'" :class="classes" tabindex="0" @click="click" :disabled="disabled" :to="to">
        <span v-if="focused" class="indicator"></span>
        <template v-if="!iconOnly">
            <i v-if="typeof leftIcon === 'string' && !loading" :class="`ti ti-${leftIcon}`"></i>
            <i v-else-if="typeof leftIcon === 'object' && !loading" :class="`ti ti-${leftIcon.icon}`" :style="{ color: leftIcon.color }"></i>
            <div v-if="loading" class="loader">
                <Loader />
            </div>
            <span class="text">
                <slot>{{ label }}</slot>
            </span>
            <i v-if="rightIcon" :class="`ti ti-${rightIcon}`"></i>
        </template>

        <template v-else class="icon-only">
            <div v-if="loading" class="loader">
                <Loader />
            </div>
            <i v-else :class="`ti ti-${iconOnly}`" :style="{ color: iconOnlyColor || '' }"></i>
        </template>

        <div v-if="badge" class="badge">{{ badge }}</div>
    </component>
</template>
<style lang="scss" scoped>
@mixin style($mainBg, $mainColor, $secondaryBg, $secondaryColor, $strokeColor) {
    // ----- style -----
    background-color: $mainBg;
    color: $mainColor;

    &.ghost {
        background-color: transparent;
        color: $secondaryColor;
    }
    &.stroke {
        background-color: $secondaryBg;
        color: $secondaryColor;
        border: solid 1px $strokeColor;
    }
    &.badge {
        border: solid 2px $strokeColor;
    }

    &.disabled {
        cursor: not-allowed;
        background-color: $bg-weak-100;
        color: $text-disabled-300;
        border: none;
        box-shadow: none;
    }

    &.focused {
        border: 1px solid $stroke-strong-900;
    }

    &.readonly {
        cursor: default;
    }

    &.shadow {
        box-shadow: var(--regular-shadow-xs, 0 1px 2px 0 #dbdfe640);
    }
}

@mixin hover($primaryBackground, $secondaryBackground, $strokeColor) {
    background-color: $primaryBackground;
    &.stroke,
    &.ghost {
        background-color: $secondaryBackground;
    }
    &.stroke {
        border: solid 1px $strokeColor;
    }

    &.focused {
        border: 1px solid $stroke-strong-900;
    }
}

@mixin size($size) {
    height: $size;
    &.iconOnly {
        width: $size;
    }
}
.ds-button {
    cursor: pointer;
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 4px;
    position: relative;
    width: fit-content;
    transition: all 0.2s ease-in;
    font-weight: 500;

    .indicator {
        top: -4px;
        right: -4px;
        width: 10px;
        height: 10px;
        border-radius: 11px;
        background-color: $bg-strong-900;
        position: absolute;
    }

    i {
        flex-grow: 0;
    }

    .loader {
        flex-grow: 0;
    }

    border-radius: $radius-md;

    &.fullWidth {
        width: 100%;
    }

    .loader,
    .text,
    :deep(.ti) {
        flex-grow: 0;
    }

    .text {
        padding: 0 4px;
    }

    &:focus-visible:not(.disabled) {
        $outlineSize: 2px;
        outline-offset: $outlineSize;
        outline: solid $outlineSize $blue-200;

        &.neutral {
            outline: solid $outlineSize $neutral-200;
        }

        &.error {
            outline: solid $outlineSize $error-200;
        }
    }

    &:hover:not(.disabled, &:focus-visible, .readonly), &.hover {
        &.primary {
            @include hover($blue-dark, $blue-lighter, $blue-light);
        }
        &.neutral {
            @include hover($bg-strong-900, $bg-weak-100, $stroke-sub-300);
        }
        &.error {
            @include hover($red-dark, $red-lighter, $red-light);
        }
        &.badge {
            @include hover(white, white, white);
        }
    }

    // ----- type -----
    &.primary {
        @include style($blue-base, $text-white-0, $bg-white-0, $blue-base, $blue-base);
    }
    &.neutral {
        @include style($bg-surface-700, $text-white-0, $bg-white-0, $text-main-900, $stroke-soft-200);
    }
    &.error {
        @include style($red-base, $text-white-0, $bg-white-0, $red-base, $red-base);
    }
    &.badge {
        @include style($bg-white-0, $bg-strong-900, $bg-white-0, $bg-strong-900, $stroke-strong-900);
    }

    &.badge {
        .badge {
            position: absolute;
            right: -12px;
            top: calc(-50% + 12px);
            width: 22px;
            height: 22px;
            background-color: $bg-strong-900;
            color: $text-white-0;
            border-radius: $radius-full;
            display: flex;
            justify-content: center;
            align-items: center;
            font-size: 12px;
            line-height: 18px;
        }
    }

    // ----- sizes -----
    &.xl {
        @include size(48px);
        @include font-size-16;
        i {
            font-size: 18px;
        }
        padding: 12px 14px;
    }
    &.lg {
        @include size(44px);
        @include font-size-14;
        i {
            font-size: 18px;
        }
        padding: 12px 14px;
    }
    &.md {
        @include size(40px);
        @include font-size-14;
        i {
            font-size: 18px;
        }
        padding: 10px 12px;
    }
    &.sm {
        @include size(36px);
        @include font-size-14;
        i {
            font-size: 18px;
        }
        padding: 8px 10px;
    }
    &.xs {
        @include size(28px);
        @include font-size-12;
        padding: 0px 7px;
        i {
            font-size: 14px;
        }
    }
}
</style>
